import { api } from './api';

export type BrandTeamSessionMode =
  | 'full-brand-pack'
  | 'ecommerce-pack'
  | 'packaging-pack'
  | 'social-launch-pack'
  | 'signage-retail-pack'
  | 'agency-presentation-pack'
  | 'apparel-merch-pack'
  | 'corporate-identity-pack'
  | 'food-beverage-pack'
  | 'book-publishing-pack'
  | 'event-tradeshow-pack'
  | 'vouchers-gift-pack';

export type BrandTeamDirection = 'safe' | 'bold' | 'premium';

export interface BrandTeamAgentLog {
  id: string;
  agentKey: string;
  agentName: string;
  stage: string;
  summary: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface BrandTeamAgentRun {
  id: string;
  agentKey: string;
  agentName: string;
  status: string;
  model: string;
  promptVersion: string;
  inputPayload: Record<string, unknown>;
  outputPayload: Record<string, unknown> | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  latencyMs: number | null;
  usageMetadata: Record<string, unknown>;
}

export interface BrandTeamAgentArtifact {
  id: string;
  agentRunId: string;
  artifactType: string;
  artifactKey: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface BrandTeamChatMessage {
  id: string;
  sender: 'user' | 'brand-manager';
  message: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface BrandTeamSession {
  id: string;
  name: string;
  status: string;
  sessionMode: BrandTeamSessionMode | string;
  businessDescription: string | null;
  objectives: string | null;
  direction: BrandTeamDirection | string;
  selectedAssetIds: string[];
  creditEstimate: number;
  creditsUsed: number;
  brandBrief: Record<string, any>;
  deliverablePlan: Record<string, any>;
  metadata: Record<string, any>;
  briefApprovedAt?: string | null;
  planApprovedAt?: string | null;
  generationStartedAt?: string | null;
  generationCompletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  agentLogs?: BrandTeamAgentLog[];
  outputs?: BrandTeamOutput[];
  agentRuns?: BrandTeamAgentRun[];
  agentArtifacts?: BrandTeamAgentArtifact[];
  chatMessages?: BrandTeamChatMessage[];
}

export interface BrandTeamOutput {
  id: string;
  mockupId: string | null;
  outputUrl: string;
  outputType: string;
  templateId: string | null;
  qaStatus: string;
  qaScore: number | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface BrandTeamStatus {
  ready: boolean;
  missing: string[];
  message: string;
  anthropicConfigured: boolean;
}

export type MockupStyle =
  | 'auto'
  | 'realism-lifestyle'
  | 'contextual-scene'
  | 'hyper-realistic'
  | 'human-interaction'
  | 'smart-dynamic'
  | 'brutalist-editorial'
  | 'studio-clean'
  | 'flat-brand'
  | 'minimal-scandinavian';

export interface MockupPreferences {
  includeBrandName?: boolean;
  includeTagline?: boolean;
  aspectRatio?: 'square' | 'landscape' | 'portrait' | 'auto';
  styleNotes?: string;
  mockupStyle?: MockupStyle;
  sceneDescription?: string;
}

export interface CreateBrandTeamSessionRequest {
  mode: BrandTeamSessionMode;
  direction: BrandTeamDirection;
  businessDescription?: string;
  objectives?: string;
  assetIds?: string[];
  useSavedBrandSettings?: boolean;
  mockupPreferences?: MockupPreferences;
}

function extractErrorMessage(error: any, fallback: string): string {
  if (error.response) {
    const data = error.response.data;
    if (error.response.status === 402) {
      return data?.error?.message || data?.message || 'Insufficient credits. Please add more credits to continue generating.';
    }
    return data?.error?.message || data?.message || fallback;
  }
  if (error.message) return error.message;
  return fallback;
}

export const brandTeamService = {
  async getStatus(): Promise<BrandTeamStatus> {
    const response = await api.get<{ success: boolean; status: BrandTeamStatus }>('/api/brand-team/status');
    return response.data.status;
  },

  async listSessions(): Promise<BrandTeamSession[]> {
    const response = await api.get<{ success: boolean; sessions: BrandTeamSession[] }>('/api/brand-team/sessions');
    return response.data.sessions;
  },

  async getSession(sessionId: string): Promise<BrandTeamSession> {
    const response = await api.get<{ success: boolean; session: BrandTeamSession }>(`/api/brand-team/sessions/${sessionId}`);
    return response.data.session;
  },

  async createSession(payload: CreateBrandTeamSessionRequest): Promise<BrandTeamSession> {
    try {
      const response = await api.post<{ success: boolean; session: BrandTeamSession }>('/api/brand-team/sessions', payload);
      return response.data.session;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to create session'));
    }
  },

  async approveBrief(sessionId: string): Promise<BrandTeamSession> {
    try {
      const response = await api.post<{ success: boolean; session: BrandTeamSession }>(`/api/brand-team/sessions/${sessionId}/approve-brief`);
      return response.data.session;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to approve brief'));
    }
  },

  async approvePlan(sessionId: string): Promise<BrandTeamSession> {
    try {
      const response = await api.post<{ success: boolean; session: BrandTeamSession }>(`/api/brand-team/sessions/${sessionId}/approve-plan`);
      return response.data.session;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to approve plan'));
    }
  },

  async shuffleDeliverable(sessionId: string, index: number): Promise<BrandTeamSession> {
    try {
      const response = await api.post<{ success: boolean; session: BrandTeamSession }>(
        `/api/brand-team/sessions/${sessionId}/shuffle-deliverable`,
        { index }
      );
      return response.data.session;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to shuffle deliverable'));
    }
  },

  async generateSession(sessionId: string): Promise<BrandTeamSession> {
    try {
      const response = await api.post<{ success: boolean; session: BrandTeamSession }>(`/api/brand-team/sessions/${sessionId}/generate`);
      return response.data.session;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to start generation'));
    }
  },

  async chatWithBrandManager(sessionId: string, message: string): Promise<BrandTeamSession> {
    try {
      const response = await api.post<{ success: boolean; session: BrandTeamSession }>(
        `/api/brand-team/sessions/${sessionId}/chat`,
        { message }
      );
      return response.data.session;
    } catch (error: any) {
      throw new Error(extractErrorMessage(error, 'Failed to send message'));
    }
  },
};
